// Импортируем наш объект с переменными окружения
import { ENV } from "../env";

export interface ILogin {
  telegram: string;
  password: string;
}

export interface ISubscriptionVariant {
  id: number;
  price: number;
}

export interface ISubscription {
  sub_name: string;
  sub_access: string;
  sub_desc_list: string[];
  variants: ISubscriptionVariant[];
}

export interface ISubscriptionsResponse {
  subscriptions: ISubscription[];
}

export interface IUserSubscription {
  subscription: string;
  end_subscription_time: string;
  is_auto_pay: boolean;
}

export interface ISubscribeResponse {
  redirect_url: string;
}

// Интерфейсы для работы с городами и станциями метро
export interface ICitiesResponse {
  cities: string[];
}

export interface IMetroStation {
  id: number;
  perspective: boolean;
  name: {
    ru: string;
    en: string;
    cn: string;
  };
  schemaName: {
    ru: string;
    en: string;
    cn: string;
  };
  lineId: number;
  location: {
    lat: number;
    lon: number;
  };
}

export interface IMetroStationsResponse {
  stations: string[];
}

// Интерфейсы для поиска закупок
export interface IPurchaseSize {
  size: string;
  price: number;
}

export interface IPurchaseSearchResponse {
  model: string;
  color: string;
  photo_url: string;
  data: IPurchaseSize[];
}

export interface SoldOutRequest {
  id: number;
  buyer: string;
}

// Интерфейсы для статусов отгрузки
export interface IShipmentStatus {
  id: number;
  status: string;
  vendor_id: number;
}

export interface ICreateShipmentStatusRequest {
  status: string;
}

export interface ICreateShipmentStatusResponse {
  id: number;
  status: string;
}

export interface ICreateShipmentRequest {
  sneaker_id: number;
  status_id: number;
}

export interface IDeleteShipmentRequest {
  sneaker_id: number;
}

// Интерфейсы для фильтрации отгрузок
export interface IModel {
  id: number;
  name: string;
}

export interface IArticle {
  id: number;
  article: string;
}

export interface INotification {
  id: number;
  title: string;
  message?: string;
  description?: string;
  component?: string;
  type?: 'info' | 'success' | 'warning' | 'error';
  is_checked: boolean;
  date: string;
  created_at?: string;
  updated_at?: string;
}

export interface INotificationResponse {
  notifications: INotification[];
}

export interface IUserBrand {
  id: number;
  brand: string;
}

export interface IModelsResponse {
  models: string[];
}

export interface IArticlesResponse {
  articles: string[];
}

// Интерфейсы для заказов закупок
export interface ICreateSupplyOrderRequest {
  productId: string;
  article: string;
  size: string;
  quantity: number;
  price: number;
  customerName: string;
  customerPhone: string;
  customerEmail?: string;
  deliveryAddress: string;
  deliveryMethod: 'pickup' | 'delivery';
  paymentMethod: 'cash' | 'card' | 'transfer';
  notes?: string;
}

export interface ISupplyOrderResponse {
  id: string;
  productId: string;
  article: string;
  size: string;
  quantity: number;
  price: number;
  status: 'pending' | 'confirmed' | 'shipped' | 'delivered' | 'cancelled';
  customerName: string;
  customerPhone: string;
  customerEmail?: string;
  deliveryAddress: string;
  deliveryMethod: 'pickup' | 'delivery';
  paymentMethod: 'cash' | 'card' | 'transfer';
  notes?: string;
  createdAt: string;
  updatedAt: string;
}

// Интерфейсы для новых заказов (API /orders/new)
export interface IOrderItem {
  article: string;
  size_eu: number;
  count: number;
}

export interface ICreateOrderRequest {
  items: IOrderItem[];
  address_id: number | null;
}

export interface ICreateOrderResponse {
  success?: boolean;
  order_id?: number;
  message?: string;
  error?: string;
}

// Интерфейсы для списка заказов (API /orders/list)
// Ответ от /orders/list
export interface IOrdersListAddress {
  address: string;
  first_name: string;
  last_name: string;
  middle_name: string;
}

// Интерфейс для элементов заказа при получении списка (включает price от сервера)
export interface IOrderItemWithPrice {
  article: string;
  size_eu: number;
  price: number;
  count: number;
}

export interface IOrdersListOrder {
  order_id: number;
  order_status: string; // "В ожидании" | "Подтвержден" | "Отправлен" | "Доставлен" | "Отменен"
  address: IOrdersListAddress;
  cancel_reason: string;
  create_time: string; // ISO date
  items: IOrderItemWithPrice[]; // Включает price от сервера
}

export interface IOrdersListResponse {
  orders: IOrdersListOrder[];
}

// Интерфейсы для поиска товаров среди продавцов
export interface IUserInfoResponse {
  user_name: string;
  email: string;
  vk_account: string | null;
  first_name: string | null;
  last_name: string | null;
  telegram_account: string | null; // New field
  phone_number: string | null; // New field
  show_vk: boolean | number | null;
  show_telegram: boolean | number | null;
  show_first_name: boolean | number | null;
  show_last_name: boolean | number | null;
  show_user_name: boolean | number | null;
  show_telegram_account: boolean | number | null;
  show_phone_number: boolean | number | null;
  // Количество оставшихся открытий контактов в поиске
  search_counter?: number; // делаем опциональным для обратной совместимости
  // Доступ к складскому функционалу
  access_stockroom?: boolean;
}

export interface IUpdateUserInfoRequest {
  user_name?: string;
  first_name?: string;
  last_name?: string;
  email?: string;
  vk_account?: string;
  telegram_account?: string;
  phone_number?: string;
}

export interface IUpdateVisionSettingsRequest {
  show_vk?: boolean;
  show_telegram?: boolean;
  show_first_name?: boolean;
  show_last_name?: boolean;
  show_user_name?: boolean;
  show_telegram_account?: boolean;
  show_phone_number?: boolean;
}

// Интерфейсы для работы с одеждой
export interface ICreateClothesRequest {
  size: string;
  brand: string;
  price: number;
  article: string;
  condition: string;
  city: string;
  place: string;
  fitting: boolean;
}

export interface IUpdateClothesRequest {
  id: number;
  size: string;
  brand: string;
  price: number;
  article: string;
  condition: string;
  city: string;
  place: string;
  fitting: boolean;
}

export interface IDeleteClothesRequest {
  id: number;
}

export interface IGetClothesRequest {
  brand?: string;
  model?: string;
  color?: string;
  size?: string;
  article?: string;
  condition?: string;
  city?: string;
  fitting?: boolean;
  offset?: number;
  limit?: number;
}

export interface IClothes {
  id: number;
  size: string | { id: number; size: string };
  brand: string | { brand: string };
  model?: string;
  color?: string;
  price: number;
  article: string;
  condition: string | { condition: string };
  city: string;
  place: string;
  fitting: boolean;
  vendor?: {
    first_name: string;
    last_name: string;
    vk_account: string;
    telegram_account: string;
    show_vk: boolean | null;
    show_telegram: boolean | null;
    show_first_name: boolean | null;
    show_last_name: boolean | null;
    show_username: boolean | null;
  };
  created_at?: string;
  updated_at?: string;
}

export interface IGetClothesItem {
  clothes: IClothes;
  photo_url: string;
}

export interface IClothesProduct {
  article: string;
  clothes: IGetClothesItem[];
}

export interface IClothesDataGroup {
  brand: string;
  products: IClothesProduct[];
}

export interface IGetClothesResponse {
  total_inventory_count: number;
  data: IClothesDataGroup[];
}

// Интерфейсы для размеров одежды
export interface ICreateSizeRequest {
  name: string;
}

export interface IDeleteSizeRequest {
  id: number;
}

export interface IEditSizeRequest {
  id: number;
  name: string;
}

export interface IGetSizeResponse {
  id: number;
  name: string;
}

export interface IAllSizeResponse {
  sizes: IGetSizeResponse[];
}

// Интерфейсы для работы с адресами
// Типы для nullable полей от Go backend
export interface NullableString {
  String: string;
  Valid: boolean;
}

export interface NullableInt64 {
  Int64: number;
  Valid: boolean;
}

export interface IAddress {
  Id: number;
  UserId: NullableInt64;
  User: {
    first_name: string;
    last_name: string;
    vk_account: string;
    telegram_account: string;
    show_vk: boolean | null;
    show_telegram: boolean | null;
    show_first_name: boolean | null;
    show_last_name: boolean | null;
    show_username: boolean | null;
  };
  address_name: string;
  first_name: NullableString;
  last_name: NullableString;
  middle_name: NullableString;
}

export interface ICreateAddressRequest {
  address: string;
  first_name: string;
  last_name: string;
  middle_name: string;
}

export interface IUpdateAddressRequest extends Partial<ICreateAddressRequest> {
}

export interface ISearchVendor {
  telegram: string;
  vk: string;
}

export interface IVendor {
  nickname: string;
}

export interface ISearchProduct {
  id: number;
  uk_size: number;
  us_size: number;
  usw_size: number;
  eu_size: number;
  brand: {
    brand: string;
  };
  model: string;
  color: string;
  price: number;
  article: string;
  condition: {
    condition: string;
  };
  city: string;
  place: string;
  fitting: boolean;
  vendor: ISearchVendor;
  photo_url: string;
}

export interface ISearchResponse {
  stock: ISearchProduct[];
  total_count: number;
}

export interface IPlacesResponse {
  places: string[];
}

export interface IStockContactResponse {
  sneaker_id: number;
  first_name: string;
  last_name: string;
  user_name: string;
  telegram: string;
  vk: string;
  search_counter: number;
}

// Интерфейс для получения информации о товаре из склада
export interface IGetStockroomSchema {
  product_brand: string;
  product_model: string;
  product_color: string;
  product_condition: string;
  product_us_size: string;
  product_sku: string;
  product_location: {
    location: string;
    letter: string;
    number: number;
  };
  product_status: string;
  product_code: string;
  product_image: string;
  product_vendor: {
    username: string;
    telegram: string;
    vk: string;
  };
}

// Интерфейс для генерации промокода товара
export interface ISuccessInitProductSchema {
  product_code: string;
  qr_code: string;
}

// Интерфейсы для работы с местами и статусами
export interface ILocation {
  id: number;
  name: string;
}

export interface IPlace {
  id: number;
  letter: string;
  number: number;
  location_id: number;
}

export interface IStatus {
  id: number;
  name: string;
}

export interface ISetPlaceRequest {
  product_code: string;
  place_id: number;
}

export interface IChangeStatusRequest {
  product_code: string;
  status_id: number;
}


// Новые интерфейсы для инвентаря
export interface ISneakerVendor {
  first_name: string;
  last_name: string;
  vk_account: string | null;
  telegram_account: string | null;
  show_vk: number | null;
  show_telegram: number | null;
}

export interface ISneakerBrand {
  brand: string;
}

export interface ISneakerCondition {
  condition: string;
}

export interface ISneakerPrices {
  goat: number;
  poizon: number;
  stock_x: number;
  avg_price: number;
}

export interface ISneaker {
  id: number;
  uk_size: number;
  us_size: number;
  eu_size: number;
  brand: ISneakerBrand;
  model: string;
  color: string;
  price: number;
  article: string;
  condition: ISneakerCondition;
  city: string;
  place: string;
  fitting: boolean;
  vendor: ISneakerVendor;
  purchase_price: number;
  purchase_date: string | null;
  in_store: boolean;
  is_sale: boolean;
  sale_date: string | null;
  provider_name?: string;
  buyer_name?: string;
  status?: string;
  shipment_set_data?: string;
  in_shipment_days?: number;
}

export interface ISneakerWithPhoto {
  sneaker: ISneaker;
  photo_url: string;
  prices: ISneakerPrices;
}

export interface IProductGroup {
  article: string;
  sneakers: ISneakerWithPhoto[];
}

export interface IBrandGroup {
  brand: string;
  products: IProductGroup[];
}

export interface IInventoryResponse {
  total_inventory_count: number;
  data: IBrandGroup[];
}

export class Api {
  constructor() {
    const apiBaseUrl = ENV.BUN_PUBLIC_API_BASE_URL;

    this.URL = apiBaseUrl;

    // Получаем актуальный токен
    this.updateToken();
  }
  URL;
  token: string | null = null;
  private isRefreshing: boolean = false;

  // Метод для получения актуального токена из localStorage
  updateToken() {
    const authStorage = localStorage.getItem("auth-token-storage");
    this.token = authStorage ? JSON.parse(authStorage).state?.token : null;
    return this.token;
  }

  async login(creds: ILogin) {
    return fetch(this.URL + "profile/sig-in", {
      method: "POST",
      body: JSON.stringify(creds),
      headers: {
        "Content-Type": "application/json",
      },
    }).then(this.getJsonResponse);
  }

  /**
   * Метод для обновления токена авторизации
   * Отправляет POST запрос на /api/profile/refresh
   * @returns Promise с новым токеном или ошибкой
   */
  async refreshToken() {
    return fetch(this.URL + "profile/refresh", {
      method: "POST",
      headers: this.getAuthHeaders(),
    }).then(this.getRefreshTokenResponse);
  }

  private async getRefreshTokenResponse(res: Response) {
    if (res.ok) {
      return res.json();
    }
    
    // Клонируем ответ, чтобы можно было прочитать тело как текст, если чтение JSON не удастся
    const clonedResponse = res.clone();

    try {
      const errorData = await res.json();
      
      // Возвращаем объект с ошибкой для обработки
      if (errorData.error) {
        return { error: errorData.error };
      } else if (errorData.message) {
        return { error: errorData.message };
      } else if (errorData.errors && Array.isArray(errorData.errors)) {
        return { error: errorData.errors.join(", ") };
      } else if (typeof errorData === "string") {
        return { error: errorData };
      } else {
        return { error: "Неизвестная ошибка обновления токена" };
      }
    } catch (e) {
      // Если не удалось распарсить JSON, используем клонированный ответ для чтения текста
      try {
        const errorText = await clonedResponse.text();
        return { error: errorText || "Ошибка обновления токена" };
      } catch (textError) {
        // Если и это не удалось, возвращаем общую информацию об ошибке
        return { error: "Не удалось обновить токен" };
      }
    }
  }

  private async getJsonResponse(res: Response) {
    if (res.ok) {
      return res.json();
    }
    if (res.status === 401) {
      // При ошибке авторизации (401 - неавторизованный доступ) удаляем токен из localStorage
      const authStorage = localStorage.getItem("auth-token-storage");
      if (authStorage) {
        const data = JSON.parse(authStorage);
        if (data.state) {
          data.state.token = null;
          localStorage.setItem("auth-token-storage", JSON.stringify(data));
        }
      }

      if (
        window.location.pathname !== "/login" &&
        window.location.pathname !== "/register"
      ) {
        window.location.href = "/login";
      }
    }
    if (res.status === 429) {
      return { error: "Слишком много запросов. Попробуйте позже." };
    }

    // Клонируем ответ, чтобы можно было прочитать тело как текст, если чтение JSON не удастся
    const clonedResponse = res.clone();

    try {
      const errorData = await res.json();

      // Для 403 ошибок выбрасываем исключение с информацией о статусе
      if (res.status === 403) {
        const error = new Error(
          errorData.error || errorData.message || "Ошибка доступа"
        );
        (error as any).response = { status: 403, data: errorData };
        throw error;
      }

      // Для 400 ошибок также выбрасываем исключение
      if (res.status === 400) {
        const error = new Error(
          errorData.error || errorData.message || "Неверный запрос"
        );
        (error as any).response = { status: 400, data: errorData };
        throw error;
      }

      // Возвращаем объект с ошибкой для обработки в useApiCall
      if (errorData.error) {
        return { error: errorData.error };
      } else if (errorData.message) {
        return { error: errorData.message };
      } else if (errorData.errors && Array.isArray(errorData.errors)) {
        return { error: errorData.errors.join(", ") };
      } else if (typeof errorData === "string") {
        return { error: errorData };
      } else {
        return { error: "Неизвестная ошибка" };
      }
    } catch (e) {
      // Если это наше исключение для 403 или 400, пробрасываем его дальше
      if (
        (e as any).response?.status === 403 ||
        (e as any).response?.status === 400
      ) {
        throw e;
      }

      // Если не удалось распарсить JSON, используем клонированный ответ для чтения текста
      try {
        const errorText = await clonedResponse.text();
        return { error: errorText || "Неизвестная ошибка" };
      } catch (textError) {
        // Если и это не удалось, возвращаем общую информацию об ошибке
        return { error: "Не удалось получить информацию об ошибке" };
      }
    }
  }

  // Метод для получения заголовков с актуальным токеном
  private getAuthHeaders() {
    // Всегда обновляем токен перед формированием заголовков
    this.updateToken();
    return {
      "Content-Type": "application/json",
      Authorization: `${this.token ?? ""}`,
    };
  }

  /**
   * Метод для выполнения запросов с автоматическим обновлением токена
   * При получении 401 ошибки автоматически пытается обновить токен
   * и повторить запрос с новым токеном
   * @param url - URL для запроса
   * @param options - Опции для fetch запроса
   * @returns Promise<Response>
   */
  private async fetchWithTokenRefresh(
    url: string,
    options: RequestInit
  ): Promise<Response> {
    // Первый запрос
    let response = await fetch(url, options);

    // Если получили 401 и это не запрос на обновление токена и мы не в процессе обновления
    if (response.status === 401 && !url.includes('profile/refresh') && !this.isRefreshing) {
      this.isRefreshing = true;
      
      try {
        // Пытаемся обновить токен
        const refreshResult = await this.refreshToken();
        if (refreshResult && !refreshResult.error && refreshResult.token) {
          // Сохраняем новый токен с меткой времени
          const authStorage = localStorage.getItem("auth-token-storage");
          if (authStorage) {
            const data = JSON.parse(authStorage);
            if (data.state) {
              data.state.token = refreshResult.token;
              data.state.tokenRefreshedAt = new Date().toISOString(); // Добавляем метку времени
              localStorage.setItem("auth-token-storage", JSON.stringify(data));
              this.token = refreshResult.token;
              
              // Обновляем заголовки с новым токеном
              if (options.headers) {
                (options.headers as any)['Authorization'] = `${this.token}`;
              }
              
              // Повторяем запрос с новым токеном
              response = await fetch(url, options);
            }
          }
        }
      } catch (refreshError) {
        console.error('Ошибка обновления токена:', refreshError);
      } finally {
        this.isRefreshing = false;
      }
    }

    return response;
  }

  async createSneakersLot(data: any) {
    const options = {
      method: "POST",
      body: JSON.stringify(data),
      headers: this.getAuthHeaders(),
    };

    return this.fetchWithTokenRefresh(this.URL + "sneakers/sneakers", options)
      .then(this.getJsonResponse);
  }
  async resetPassword({
    telegram,
    ott_code,
    new_password,
  }: {
    telegram: string;
    new_password: string;
    ott_code: string;
  }) {
    return fetch(this.URL + "profile/reset-password", {
      method: "POST",
      body: JSON.stringify({ telegram, ott_code, new_password }),
      headers: this.getAuthHeaders(),
    }).then(this.getJsonResponse);
  }

  async getBrands() {
    return fetch(`${ENV.BUN_PUBLIC_API_BASE_URL}brands/brand`, {
      method: "GET",
      headers: this.getAuthHeaders(),
    }).then(this.getJsonResponse);
  }

  async getConditions() {
    return fetch(`${ENV.BUN_PUBLIC_API_BASE_URL}conditions/condition`, {
      method: "GET",
      headers: this.getAuthHeaders(),
    }).then(this.getJsonResponse);
  }

  async getSneakers(
    offset: number = 0,
    limit: number = 20,
    isSale?: boolean,
    system: string = "inventory",
    brandId?: number,
    model?: string,
    article?: string,
    size?: string,
    priceFrom?: number,
    priceTo?: number
  ): Promise<IInventoryResponse> {
    const params = new URLSearchParams({
      offset: offset.toString(),
      limit: limit.toString(),
      system: system,
    });

    // Добавляем параметры фильтрации
    if (isSale !== undefined) {
      params.set("is_sale", isSale ? "true" : "false");
    }
    if (brandId) {
      params.set("brand", brandId.toString());
    }
    if (model) {
      params.set("model", model);
    }
    if (article) {
      params.set("article", article);
    }
    if (size) {
      params.set("size_us", size);
    }
    if (priceFrom !== undefined) {
      params.set("price_from", priceFrom.toString());
    }
    if (priceTo !== undefined) {
      params.set("price_to", priceTo.toString());
    }

    const options = {
      method: "GET",
      headers: this.getAuthHeaders(),
    };

    return this.fetchWithTokenRefresh(`${this.URL}sneakers/sneakers?${params}`, options)
      .then(this.getJsonResponse);
  }

  async getShipments(
    offset: number = 0,
    limit: number = 20,
    article?: string,
    model?: string,
    priceFrom?: number,
    priceTo?: number,
    daysOnShipment?: number,
    shipmentStatusId?: number
  ): Promise<IInventoryResponse> {
    const params = new URLSearchParams({
      offset: offset.toString(),
      limit: limit.toString(),
      system: "shipment",
    });

    // Добавляем параметры фильтрации
    if (article) {
      params.set("article", article);
    }
    if (model) {
      params.set("model", model);
    }
    if (priceFrom !== undefined) {
      params.set("price_from", priceFrom.toString());
    }
    if (priceTo !== undefined) {
      params.set("price_to", priceTo.toString());
    }
    if (daysOnShipment !== undefined) {
      params.set("days_on_shipment", daysOnShipment.toString());
    }
    if (shipmentStatusId !== undefined) {
      params.set("shipment_status", shipmentStatusId.toString());
    }

    const options = {
      method: "GET",
      headers: this.getAuthHeaders(),
    };

    return this.fetchWithTokenRefresh(`${this.URL}sneakers/sneakers?${params}`, options)
      .then(this.getJsonResponse);
  }

  async searchSneakers(
    query: string,
    isSale: boolean = false,
    limit: number = 20,
    offset: number = 0
  ): Promise<IInventoryResponse> {
    const params = new URLSearchParams({
      q: query,
      is_sale: isSale.toString(),
      limit: limit.toString(),
      offset: offset.toString(),
    });

    const options = {
      method: "GET",
      headers: this.getAuthHeaders(),
    };

    return this.fetchWithTokenRefresh(`${this.URL}sneakers/search?${params}`, options)
      .then(this.getJsonResponse);
  }

  // Получение списка статусов отгрузки
  async getShipmentStatuses(): Promise<IShipmentStatus[]> {
    const options = {
      method: "GET",
      headers: this.getAuthHeaders(),
    };

    return this.fetchWithTokenRefresh(`${this.URL}shipments/status`, options)
      .then(this.getJsonResponse);
  }

  // Создание нового статуса отгрузки
  async createShipmentStatus(data: ICreateShipmentStatusRequest): Promise<ICreateShipmentStatusResponse> {
    const options = {
      method: "POST",
      headers: this.getAuthHeaders(),
      body: JSON.stringify(data),
    };

    return this.fetchWithTokenRefresh(`${this.URL}shipments/status/new`, options)
      .then(this.getJsonResponse);
  }

  // Создание отгрузки
  async createShipment(data: ICreateShipmentRequest): Promise<void> {
    const options = {
      method: "POST",
      headers: this.getAuthHeaders(),
      body: JSON.stringify(data),
    };

    return this.fetchWithTokenRefresh(`${this.URL}shipments/sneakers/new`, options)
      .then(() => {}); // API возвращает пустой ответ
  }

  // Удаление товара из отгрузки (перемещение в инвентарь)
  async deleteShipment(data: IDeleteShipmentRequest): Promise<void> {
    const options = {
      method: "POST",
      headers: this.getAuthHeaders(),
      body: JSON.stringify(data),
    };

    return this.fetchWithTokenRefresh(`${this.URL}shipments/sneakers/delete`, options)
      .then(() => {}); // API возвращает пустой ответ
  }

  // Получение моделей для фильтрации
  async getSneakerModels(): Promise<IModelsResponse> {
    const options = {
      method: "GET",
      headers: this.getAuthHeaders(),
    };

    return this.fetchWithTokenRefresh(`${this.URL}sneakers/models`, options)
      .then(this.getJsonResponse);
  }

  // Получение артикулов для фильтрации
  async getSneakerArticles(): Promise<IArticlesResponse> {
    const options = {
      method: "GET",
      headers: this.getAuthHeaders(),
    };

    return this.fetchWithTokenRefresh(`${this.URL}sneakers/articles`, options)
      .then(this.getJsonResponse);
  }

  // Получение брендов пользователя
  async getUserBrands(): Promise<IUserBrand[]> {
    const options = {
      method: "GET",
      headers: this.getAuthHeaders(),
    };

    return this.fetchWithTokenRefresh(`${this.URL}brands/user_brands`, options)
      .then(this.getJsonResponse);
  }

  // Методы для работы с уведомлениями
  async getAllNotifications(): Promise<INotificationResponse> {
    const options = {
      method: "GET",
      headers: this.getAuthHeaders(),
    };

    return this.fetchWithTokenRefresh(`${this.URL}notifications/all`, options)
      .then(this.getJsonResponse);
  }

  async getUnreadNotifications(): Promise<INotificationResponse> {
    const options = {
      method: "GET",
      headers: this.getAuthHeaders(),
    };

    return this.fetchWithTokenRefresh(`${this.URL}notifications/not-checked`, options)
      .then(this.getJsonResponse);
  }

  async getNotificationById(id: number): Promise<INotification> {
    const options = {
      method: "GET",
      headers: this.getAuthHeaders(),
    };

    return this.fetchWithTokenRefresh(`${this.URL}notifications/${id}`, options)
      .then(this.getJsonResponse);
  }

  async markNotificationAsRead(id: number): Promise<{ success: boolean }> {
    const options = {
      method: "POST",
      headers: this.getAuthHeaders(),
    };

    return this.fetchWithTokenRefresh(`${this.URL}notifications/${id}`, options)
      .then((response) => {
        // Для этого API не ожидаем JSON ответ, просто проверяем статус
        if (response.ok) {
          return { success: true };
        } else {
          return { success: false };
        }
      });
  }

  async getUserInfo(): Promise<IUserInfoResponse> {
    const options = {
      method: "GET",
      headers: this.getAuthHeaders(),
    };

    return this.fetchWithTokenRefresh(`${this.URL}profile/get-user-info`, options)
      .then(this.getJsonResponse);
  }

  async getStatisticData() {
    this.updateToken();
    return fetch(this.URL + "statistic", {
      method: "GET",
      headers: this.getAuthHeaders(),
    }).then(this.getJsonResponse);
  }

  // Новые методы для аналитики
  async getAnalyticInventory(period?: "week" | "month" | "year") {
    this.updateToken();
    return fetch(this.URL + "analytic/inventory", {
      method: "GET",
      headers: this.getAuthHeaders(),
    }).then(this.getJsonResponse);
  }

  async getSalesDates(period: "week" | "month" | "year") {
    this.updateToken();
    return fetch(this.URL + "analytic/sales/dates", {
      method: "POST",
      body: JSON.stringify({ period }),
      headers: {
        ...this.getAuthHeaders(),
        "Content-Type": "application/json",
      },
    }).then(this.getJsonResponse);
  }

  async getSalesProfit(period: "week" | "month" | "year") {
    this.updateToken();
    return fetch(this.URL + "analytic/sales/profit", {
      method: "POST",
      body: JSON.stringify({ period }),
      headers: {
        ...this.getAuthHeaders(),
        "Content-Type": "application/json",
      },
    }).then(this.getJsonResponse);
  }

  async getSalesAbsProfit(period: "week" | "month" | "year") {
    this.updateToken();
    return fetch(this.URL + "analytic/sales/abs-profit", {
      method: "POST",
      body: JSON.stringify({ period }),
      headers: {
        ...this.getAuthHeaders(),
        "Content-Type": "application/json",
      },
    }).then(this.getJsonResponse);
  }

  async getAllSales(period: "week" | "month" | "year") {
    this.updateToken();
    return fetch(this.URL + "analytic/sales/all-sales", {
      method: "POST",
      body: JSON.stringify({ period }),
      headers: {
        ...this.getAuthHeaders(),
        "Content-Type": "application/json",
      },
    }).then(this.getJsonResponse);
  }

  async getSalesBySize(period: "week" | "month" | "year") {
    this.updateToken();
    return fetch(this.URL + "analytic/sales/by-size", {
      method: "POST",
      body: JSON.stringify({ period }),
      headers: {
        ...this.getAuthHeaders(),
        "Content-Type": "application/json",
      },
    }).then(this.getJsonResponse);
  }

  async getSalesByBrand(period: "week" | "month" | "year") {
    this.updateToken();
    return fetch(this.URL + "analytic/sales/by-brand", {
      method: "POST",
      body: JSON.stringify({ period }),
      headers: {
        ...this.getAuthHeaders(),
        "Content-Type": "application/json",
      },
    }).then(this.getJsonResponse);
  }

  async getSalesByArticle(period: "week" | "month" | "year") {
    this.updateToken();
    return fetch(this.URL + "analytic/sales/by-article", {
      method: "POST",
      body: JSON.stringify({ period }),
      headers: {
        ...this.getAuthHeaders(),
        "Content-Type": "application/json",
      },
    }).then(this.getJsonResponse);
  }

  async getSalesByModel(period: "week" | "month" | "year") {
    this.updateToken();
    return fetch(this.URL + "analytic/sales/by-model", {
      method: "POST",
      body: JSON.stringify({ period }),
      headers: {
        ...this.getAuthHeaders(),
        "Content-Type": "application/json",
      },
    }).then(this.getJsonResponse);
  }

  async getSalesByArticleAndSize(period: "week" | "month" | "year") {
    this.updateToken();
    return fetch(this.URL + "analytic/sales/by-article-and-size", {
      method: "POST",
      body: JSON.stringify({ period }),
      headers: {
        ...this.getAuthHeaders(),
        "Content-Type": "application/json",
      },
    }).then(this.getJsonResponse);
  }

  async getBestSales(period: "week" | "month" | "year") {
    this.updateToken();
    return fetch(this.URL + "analytic/sales/best-sales", {
      method: "POST",
      body: JSON.stringify({ period }),
      headers: {
        ...this.getAuthHeaders(),
        "Content-Type": "application/json",
      },
    }).then(this.getJsonResponse);
  }

  async updateUserInfo(data: IUpdateUserInfoRequest) {
    return fetch(`${this.URL}profile/update`, {
      method: "POST",
      body: JSON.stringify(data),
      headers: this.getAuthHeaders(),
    }).then(this.getJsonResponse);
  }

  async updateVisionSettings(data: IUpdateVisionSettingsRequest) {
    return fetch(`${this.URL}profile/update-vision`, {
      method: "POST",
      body: JSON.stringify(data),
      headers: this.getAuthHeaders(),
    }).then(this.getJsonResponse);
  }
  async updateUserPassword(data: {
    old_password: string;
    new_password: string;
  }) {
    return fetch(`${this.URL}profile/change-password`, {
      method: "POST",
      body: JSON.stringify(data),
      headers: this.getAuthHeaders(),
    }).then(this.getPasswordChangeResponse);
  }

  private async getPasswordChangeResponse(res: Response) {
    if (res.ok) {
      return res.json();
    }

    // Клонируем ответ для чтения тела
    const clonedResponse = res.clone();

    try {
      const errorData = await res.json();

      // Возвращаем объект с ошибкой без автоматического logout
      if (errorData.error) {
        return { error: errorData.error };
      } else if (errorData.message) {
        return { error: errorData.message };
      } else if (errorData.errors && Array.isArray(errorData.errors)) {
        return { error: errorData.errors.join(", ") };
      } else if (typeof errorData === "string") {
        return { error: errorData };
      } else {
        return { error: "Неизвестная ошибка при смене пароля" };
      }
    } catch (e) {
      // Если не удалось распарсить JSON
      try {
        const errorText = await clonedResponse.text();
        return { error: errorText || "Ошибка смены пароля" };
      } catch (textError) {
        return {
          error:
            "Не удалось сменить пароль. Проверьте правильность старого пароля.",
        };
      }
    }
  }
  async putInStore(id: number) {
    return fetch(`${this.URL}shipments/sneakers/delete`, {
      method: "POST",
      body: JSON.stringify({ sneaker_id: id }),
      headers: this.getAuthHeaders(),
    }).then(async (res) => {
      if (res.ok) {
        // Проверяем, есть ли контент в ответе
        const text = await res.text();
        return text ? JSON.parse(text) : {};
      }
      return this.getJsonResponse(res);
    });
  }
  async setSoldOut(data: SoldOutRequest) {
    return fetch(`${this.URL}sneakers/sneakers/sold_out/${data.id}`, {
      method: "POST",
      body: JSON.stringify({ buyer: data.buyer }),
      headers: this.getAuthHeaders(),
    }).then(this.getJsonResponse);
  }
  async deleteFromStore(id: number) {
    return fetch(`${this.URL}sneakers/sneakers/delete_from_store/${id}`, {
      method: "POST",
      headers: this.getAuthHeaders(),
    }).then(this.getJsonResponse);
  }

  async getSneakerById(id: number) {
    return fetch(`${this.URL}sneakers/sneakers/${id}`, {
      method: "GET",
      headers: this.getAuthHeaders(),
    }).then(this.getJsonResponse);
  }

  async updateSneaker(data: any) {
    return fetch(`${this.URL}sneakers/sneakers`, {
      method: "PUT",
      body: JSON.stringify(data),
      headers: this.getAuthHeaders(),
    }).then(this.getJsonResponse);
  }

  async deleteSneaker(id: number) {
    return fetch(`${this.URL}sneakers/sneakers`, {
      method: "DELETE",
      body: JSON.stringify({ id }),
      headers: this.getAuthHeaders(),
    }).then(this.getJsonResponse);
  }

  async signUp(data: {
    user_name: string;
    email: string;
    telegram: string;
    password: string;
  }) {
    return fetch(this.URL + "profile/sig-up", {
      method: "POST",
      body: JSON.stringify(data),
      headers: {
        "Content-Type": "application/json",
      },
    }).then(this.getJsonResponse);
  }

  async loadProductInfo(article: string) {
    return fetch(`${ENV.BUN_PUBLIC_API_BASE_URL}load/load`, {
      method: "POST",
      body: JSON.stringify({ article }),
      headers: this.getAuthHeaders(),
    }).then(this.getJsonResponse);
  }

  // Методы для работы с подписками
  async getSubscriptions() {
    return fetch(`${this.URL}sub/subscriptions`, {
      method: "GET",
      headers: this.getAuthHeaders(),
    }).then(this.getJsonResponse);
  }

  async getUserSubscription() {
    return fetch(`${this.URL}sub/get-user-subscriptions`, {
      method: "GET",
      headers: this.getAuthHeaders(),
    }).then(this.getJsonResponse);
  }

  async subscribe(subId: number) {
    return fetch(`${this.URL}sub/subscribe`, {
      method: "POST",
      body: JSON.stringify({ sub_id: subId }),
      headers: this.getAuthHeaders(),
    }).then(this.getJsonResponse);
  }

  async unsubscribe() {
    return fetch(`${this.URL}sub/unsubscribe`, {
      method: "POST",
      headers: this.getAuthHeaders(),
    }).then(this.getJsonResponse);
  }

  async enableAutoPay() {
    return fetch(`${this.URL}sub/enable-auto-pay`, {
      method: "POST",
      headers: this.getAuthHeaders(),
    }).then(this.getJsonResponse);
  }

  async getAvailableCities(): Promise<ICitiesResponse> {
    return fetch(`${this.URL}cities/available`, {
      method: "GET",
      headers: this.getAuthHeaders(),
    }).then(this.getJsonResponse);
  }

  async getMetroStations(): Promise<IMetroStationsResponse> {
    return fetch(`${this.URL}cities/metro-stations`, {
      method: "GET",
      headers: this.getAuthHeaders(),
    }).then(this.getJsonResponse);
  }

  async importFromWarehouse(credentials: { login: string; password: string }) {
    return fetch(`${this.URL}moy_sclad/load`, {
      method: "POST",
      body: JSON.stringify({
        moy_sclad_login: credentials.login,
        moy_sclad_password: credentials.password,
      }),
      headers: this.getAuthHeaders(),
    }).then(this.getJsonResponse);
  }

  async importFromExcel(file: File) {
    const formData = new FormData();
    formData.append("file", file);

    // Обновляем токен перед запросом
    this.updateToken();

    return fetch("https://engine.legitfam.pro/api/excel/upload", {
      method: "POST",
      body: formData,
      headers: {
        Authorization: `${this.token ?? ""}`,
      },
    }).then(this.getJsonResponse);
  }

  async getProviders() {
    return fetch(`${this.URL}sellers/providers`, {
      method: "GET",
      headers: this.getAuthHeaders(),
    }).then(this.getJsonResponse);
  }

  async getBuyers() {
    return fetch(`${this.URL}sellers/buyer`, {
      method: "GET",
      headers: this.getAuthHeaders(),
    }).then(this.getJsonResponse);
  }

  // Методы для поиска товаров среди продавцов
  async searchProductsInStock(
    query: string,
    limit: number = 16,
    offset: number = 0,
    vendor?: string,
    condition?: string,
    sortOrder?: "asc" | "desc",
    sizes?: string[],
    place?: string
  ): Promise<ISearchResponse> {
    const baseUrl = "https://engine.legitfam.pro/api/stock/search";
    const params = new URLSearchParams({
      query,
      limit: limit.toString(),
      offset: offset.toString(),
    });

    if (vendor !== undefined && vendor !== null && vendor.trim() !== "") {
      params.append("vendor", vendor);
    }

    if (
      condition !== undefined &&
      condition !== null &&
      condition.trim() !== ""
    ) {
      params.append("condition", condition);
    }

    if (sortOrder) {
      params.append("price_sort", sortOrder);
    }

    if (sizes && sizes.length > 0) {
      sizes.forEach((size) => params.append("sizes", size));
    }

    if (place !== undefined && place !== null && place.trim() !== "") {
      params.append("place", place);
    }

    return fetch(`${baseUrl}?${params}`, {
      method: "GET",
      headers: this.getAuthHeaders(),
    }).then(this.getJsonResponse);
  }

  async getAllProductsInStock(
    limit: number = 16,
    offset: number = 0,
    vendor?: string,
    condition?: string,
    sortOrder?: "asc" | "desc",
    sizes?: string[],
    place?: string
  ): Promise<ISearchResponse> {
    const baseUrl = "https://engine.legitfam.pro/api/stock/all-in-stock";
    const params = new URLSearchParams({
      limit: limit.toString(),
      offset: offset.toString(),
    });

    if (vendor !== undefined && vendor !== null && vendor.trim() !== "") {
      params.append("vendor", vendor);
    }

    if (
      condition !== undefined &&
      condition !== null &&
      condition.trim() !== ""
    ) {
      params.append("condition", condition);
    }

    if (sortOrder) {
      params.append("price_sort", sortOrder);
    }

    if (sizes && sizes.length > 0) {
      sizes.forEach((size) => params.append("sizes", size));
    }

    if (place !== undefined && place !== null && place.trim() !== "") {
      params.append("place", place);
    }

    return fetch(`${baseUrl}?${params}`, {
      method: "GET",
      headers: this.getAuthHeaders(),
    }).then(this.getJsonResponse);
  }

  async getStockPlaces(): Promise<IPlacesResponse> {
    const baseUrl = "https://engine.legitfam.pro/api/stock/places";

    return fetch(baseUrl, {
      method: "GET",
      headers: this.getAuthHeaders(),
    }).then(this.getJsonResponse);
  }

  async getVendors(): Promise<IVendor[]> {
    const baseUrl = "https://engine.legitfam.pro/api/stock/vendors";

    return fetch(baseUrl, {
      method: "GET",
      headers: this.getAuthHeaders(),
    }).then(this.getJsonResponse);
  }

  async getAnalyticProviders(period: "week" | "month" | "year") {
    return fetch(`${this.URL}analytic/sales/providers`, {
      method: "POST",
      headers: {
        ...this.getAuthHeaders(),
        "Content-Type": "application/json",
      },
      body: JSON.stringify({ period }),
    }).then(this.getJsonResponse);
  }

  async getAnalyticBuyers(period: "week" | "month" | "year") {
    return fetch(`${this.URL}analytic/sales/buyers`, {
      method: "POST",
      headers: {
        ...this.getAuthHeaders(),
        "Content-Type": "application/json",
      },
      body: JSON.stringify({ period }),
    }).then(this.getJsonResponse);
  }

  async exportToExcel() {
    // Обновляем токен перед запросом
    this.updateToken();

    const response = await fetch(
      "https://engine.legitfam.pro/api/excel/export",
      {
        method: "GET",
        headers: {
          Authorization: `${this.token ?? ""}`,
        },
      }
    );

    if (!response.ok) {
      throw new Error("Ошибка при экспорте данных");
    }

    // Возвращаем blob для скачивания файла
    return response.blob();
  }

  async searchPurchasesByArticle(article: string): Promise<IPurchaseSearchResponse> {
    const baseUrl = "https://engine.legitfam.pro/api/purchases/get_purchases";
    const params = new URLSearchParams({
      article: article.trim(),
    });

    return fetch(`${baseUrl}?${params}`, {
      method: "GET",
      headers: this.getAuthHeaders(),
    }).then(this.getJsonResponse);
  }

  // Методы для работы с заказами закупок
  async createSupplyOrder(orderData: ICreateSupplyOrderRequest): Promise<ISupplyOrderResponse> {
    return fetch(`${this.URL}supply/orders`, {
      method: "POST",
      headers: {
        ...this.getAuthHeaders(),
        "Content-Type": "application/json",
      },
      body: JSON.stringify(orderData),
    }).then(this.getJsonResponse);
  }

  // Метод для создания нового заказа
  async createOrder(orderData: ICreateOrderRequest): Promise<ICreateOrderResponse> {
    const options = {
      method: "POST",
      headers: this.getAuthHeaders(),
      body: JSON.stringify(orderData),
    };

    return this.fetchWithTokenRefresh(`${this.URL}orders/new`, options)
      .then(this.getJsonResponse);
  }

  // Метод получения списка заказов
  async getOrdersList(limit: number = 5, offset: number = 0): Promise<IOrdersListResponse> {
    const params = new URLSearchParams({
      limit: limit.toString(),
      offset: offset.toString(),
    });

    const options = {
      method: "GET",
      headers: this.getAuthHeaders(),
    };

    return this.fetchWithTokenRefresh(`${this.URL}orders/list?${params}`, options)
      .then(this.getJsonResponse);
  }

  async getSupplyOrders(): Promise<ISupplyOrderResponse[]> {
    return fetch(`${this.URL}supply/orders`, {
      method: "GET",
      headers: this.getAuthHeaders(),
    }).then(this.getJsonResponse);
  }

  async updateSupplyOrder(orderId: string, updates: Partial<ICreateSupplyOrderRequest>): Promise<ISupplyOrderResponse> {
    return fetch(`${this.URL}supply/orders/${orderId}`, {
      method: "PATCH",
      headers: {
        ...this.getAuthHeaders(),
        "Content-Type": "application/json",
      },
      body: JSON.stringify(updates),
    }).then(this.getJsonResponse);
  }

  async deleteSupplyOrder(orderId: string): Promise<void> {
    return fetch(`${this.URL}supply/orders/${orderId}`, {
      method: "DELETE",
      headers: this.getAuthHeaders(),
    }).then(this.getJsonResponse);
  }

  // Методы для работы с адресами
  async createAddress(addressData: ICreateAddressRequest): Promise<IAddress> {
    return fetch(`${this.URL}address/new`, {
      method: "POST",
      headers: {
        ...this.getAuthHeaders(),
        "Content-Type": "application/json",
      },
      body: JSON.stringify(addressData),
    }).then(this.getJsonResponse);
  }

  async getAddresses(): Promise<IAddress[]> {
    return fetch(`${this.URL}address/list`, {
      method: "GET",
      headers: this.getAuthHeaders(),
    }).then(this.getJsonResponse);
  }

  async updateAddress(addressId: number, updates: IUpdateAddressRequest): Promise<IAddress> {
    return fetch(`${this.URL}address/${addressId}`, {
      method: "PATCH",
      headers: {
        ...this.getAuthHeaders(),
        "Content-Type": "application/json",
      },
      body: JSON.stringify(updates),
    }).then(this.getJsonResponse);
  }

  async deleteAddress(addressId: number): Promise<void> {
    return fetch(`${this.URL}address/${addressId}`, {
      method: "DELETE",
      headers: this.getAuthHeaders(),
    }).then(this.getJsonResponse);
  }

  // Методы для работы с одеждой
  async createClothes(data: ICreateClothesRequest): Promise<IClothes> {
    const options = {
      method: "POST",
      body: JSON.stringify(data),
      headers: this.getAuthHeaders(),
    };

    return this.fetchWithTokenRefresh(`${this.URL}clothes/clothes`, options)
      .then(this.getJsonResponse);
  }

  async updateClothes(data: IUpdateClothesRequest): Promise<IClothes> {
    const options = {
      method: "PUT",
      body: JSON.stringify(data),
      headers: this.getAuthHeaders(),
    };

    return this.fetchWithTokenRefresh(`${this.URL}clothes/clothes`, options)
      .then(this.getJsonResponse);
  }

  async deleteClothes(id: number): Promise<void> {
    const options = {
      method: "DELETE",
      body: JSON.stringify({ id }),
      headers: this.getAuthHeaders(),
    };

    return this.fetchWithTokenRefresh(`${this.URL}clothes/clothes`, options)
      .then(this.getJsonResponse);
  }

  async getClothesById(id: number): Promise<IGetClothesItem> {
    const options = {
      method: "GET",
      headers: this.getAuthHeaders(),
    };

    return this.fetchWithTokenRefresh(`${this.URL}clothes/clothes/${id}`, options)
      .then(this.getJsonResponse);
  }

  async getClothes(filters?: IGetClothesRequest): Promise<IGetClothesResponse> {
    const params = new URLSearchParams();
    
    if (filters) {
      Object.entries(filters).forEach(([key, value]) => {
        if (value !== undefined && value !== null && value !== '') {
          params.append(key, value.toString());
        }
      });
    }

    const url = params.toString() 
      ? `${this.URL}clothes/clothes?${params}` 
      : `${this.URL}clothes/clothes`;

    const options = {
      method: "GET",
      headers: this.getAuthHeaders(),
    };

    return this.fetchWithTokenRefresh(url, options)
      .then(this.getJsonResponse);
  }

  // Методы для работы с размерами одежды
  async createSize(data: ICreateSizeRequest): Promise<IGetSizeResponse> {
    const options = {
      method: "POST",
      body: JSON.stringify(data),
      headers: this.getAuthHeaders(),
    };

    return this.fetchWithTokenRefresh(`${this.URL}sizes/size`, options)
      .then(this.getJsonResponse);
  }

  async updateSize(data: IEditSizeRequest): Promise<IGetSizeResponse> {
    const options = {
      method: "PUT",
      body: JSON.stringify(data),
      headers: this.getAuthHeaders(),
    };

    return this.fetchWithTokenRefresh(`${this.URL}sizes/size`, options)
      .then(this.getJsonResponse);
  }

  async deleteSize(id: number): Promise<void> {
    const options = {
      method: "DELETE",
      body: JSON.stringify({ id }),
      headers: this.getAuthHeaders(),
    };

    return this.fetchWithTokenRefresh(`${this.URL}sizes/size`, options)
      .then(this.getJsonResponse);
  }

  async getSizeById(id: number): Promise<IGetSizeResponse> {
    const options = {
      method: "GET",
      headers: this.getAuthHeaders(),
    };

    return this.fetchWithTokenRefresh(`${this.URL}sizes/size/${id}`, options)
      .then(this.getJsonResponse);
  }

  async getSizes(): Promise<IAllSizeResponse> {
    const options = {
      method: "GET",
      headers: this.getAuthHeaders(),
    };

    return this.fetchWithTokenRefresh(`${this.URL}sizes/size`, options)
      .then(this.getJsonResponse);
  }

  async getStockContact(productId: number): Promise<IStockContactResponse> {
    const baseUrl = "https://engine.legitfam.pro/api/stock/contact";
    const params = new URLSearchParams({
      product_id: productId.toString(),
    });

    return fetch(`${baseUrl}?${params}`, {
      method: "GET",
      headers: this.getAuthHeaders(),
    }).then(this.getJsonResponse);
  }

  // Метод для получения информации о товаре из склада по коду товара
  async getStockroomItem(productCode: string): Promise<IGetStockroomSchema> {
    const baseUrl = "https://engine.legitfam.pro/api/stockroom/item";
    
    return fetch(`${baseUrl}/${productCode}`, {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
      },
    }).then(this.getJsonResponse);
  }

  // Метод для генерации промокода товара
  async initProductInStockroom(productId: number): Promise<ISuccessInitProductSchema> {
    const baseUrl = "https://engine.legitfam.pro/api/stockroom/init_in_stockroom";
    
    return fetch(`${baseUrl}/${productId}`, {
      method: "POST",
      headers: this.getAuthHeaders(),
    }).then(this.getJsonResponse);
  }

  // Метод для генерации промокода товара по product_code
  async initProductInStockroomByCode(productCode: string): Promise<ISuccessInitProductSchema> {
    const baseUrl = "https://engine.legitfam.pro/api/stockroom/init_in_stockroom";
    
    return fetch(`${baseUrl}/${productCode}`, {
      method: "POST",
      headers: this.getAuthHeaders(),
    }).then(this.getJsonResponse);
  }

  // Методы для работы с местами и статусами
  async getLocations(): Promise<ILocation[]> {
    const baseUrl = "https://engine.legitfam.pro/api/stockroom/locations";
    
    return fetch(baseUrl, {
      method: "GET",
      headers: this.getAuthHeaders(),
    }).then(this.getJsonResponse);
  }

  async getPlaces(locationId: number): Promise<IPlace[]> {
    const baseUrl = "https://engine.legitfam.pro/api/stockroom/places";
    
    return fetch(`${baseUrl}?location_id=${locationId}`, {
      method: "GET",
      headers: this.getAuthHeaders(),
    }).then(this.getJsonResponse);
  }

  async getStatuses(): Promise<IStatus[]> {
    const baseUrl = "https://engine.legitfam.pro/api/stockroom/status";
    
    const response = await fetch(baseUrl, {
      method: "GET",
      headers: this.getAuthHeaders(),
    }).then(this.getJsonResponse);
    
    // API возвращает { statuses: [...] }
    return response.statuses || response;
  }

  async setPlace(request: ISetPlaceRequest): Promise<any> {
    const baseUrl = "https://engine.legitfam.pro/api/stockroom/set-place";
    
    const response = await fetch(baseUrl, {
      method: "POST",
      headers: {
        ...this.getAuthHeaders(),
        "Content-Type": "application/json",
      },
      body: JSON.stringify(request),
    });

    // Если ответ пустой или статус 204 (No Content), возвращаем success
    if (response.status === 204 || response.status === 200) {
      const text = await response.text();
      if (!text) {
        return { success: true };
      }
      return JSON.parse(text);
    }

    return this.getJsonResponse(response);
  }

  async setStatus(request: IChangeStatusRequest): Promise<any> {
    const baseUrl = "https://engine.legitfam.pro/api/stockroom/set-status";
    
    const response = await fetch(baseUrl, {
      method: "POST",
      headers: {
        ...this.getAuthHeaders(),
        "Content-Type": "application/json",
      },
      body: JSON.stringify(request),
    });

    // Если ответ пустой или статус 204 (No Content), возвращаем success
    if (response.status === 204 || response.status === 200) {
      const text = await response.text();
      if (!text) {
        return { success: true };
      }
      return JSON.parse(text);
    }

    return this.getJsonResponse(response);
  }

}

// Утилитные функции для работы с nullable полями
export const getNullableString = (nullable: NullableString | string): string => {
  if (typeof nullable === 'string') {
    return nullable;
  }
  return nullable?.Valid ? nullable.String : '';
};

export const getNullableInt64 = (nullable: NullableInt64 | number): number => {
  if (typeof nullable === 'number') {
    return nullable;
  }
  return nullable?.Valid ? nullable.Int64 : 0;
};

export const api = new Api();
